import {ACTIVITY_TYPE, AllActivityTypes, AllActivityTypesArray, TRAVEL_OPTION_TYPE} from "../../types-enums/activity";
import {getHotelDurationInNightsWithOptionalOriginalHotelStay} from "../getter/hotel";
import {
    isActivityCarBusState,
    isActivityFlightState,
    isActivityHotelState,
    isActivityWithReturnRouteProps,
    isFlightCalculationRequestLeg
} from "../check/type-guards";
import {ActivityFlightState} from "../../types-enums/activity-flight";
import {ActivityCarBusState} from "../../types-enums/activity-car-bus";
import {ActivityHotelState} from "../../types-enums/activity-hotel";
import {
    adjustDistanceOnAddToDay,
    adjustDurationOnAddToDay,
    changeDateOnDayChange,
    recalculateActivityOnDifferentDate
} from "../check/activities";
import {ActivityCommonProperties, DurationProps} from "../../types-enums/activity-common-properties";
import cloneDeep from "lodash/cloneDeep";
import {convertDateTimeToFlightDate} from "../converter/dates";
import {hasExactDates} from "../getter/journey";
import {DateTime} from "luxon";
import {INITIAL_WAYPOINTS} from "../../components/activity/car-bus/ChooseCarBus";
import {v4 as uuidv4} from "uuid";
import {setCarBusDurationToOneWhenAddingToJourney} from "../converter/car-bus";
import {setDurationOfHotelToOneWhenAddingToJourney} from "../converter/hotel";

/**
 * Sets common properties for a flight activity.
 *
 * This function maps the activity type and travel option type for a flight activity
 * to predefined constants. It expects an object with specific properties and returns
 * an object containing these mapped properties.
 *
 * @param {Object} param - Input object containing flight activity properties.
 * @param {ActivityFlightState["common"]["activityType"]} param.activityType - The activity type associated with the flight.
 * @param {ActivityFlightState["common"]["travelOptionType"]} param.travelOptionType - The travel option type associated with the flight.
 * @returns {Object} - An object with mapped common flight activity properties.
 * @returns {ACTIVITY_TYPE.flight} return.activityType - Mapped constant for flight activity type.
 * @returns {TRAVEL_OPTION_TYPE.transport} return.travelOptionType - Mapped constant for transport travel option type.
 */
export const setCommonFlightActivityProps = ():
    {
        activityType: ActivityFlightState["common"]["activityType"],
        travelOptionType: ActivityFlightState["common"]["travelOptionType"]
    } => ({
    activityType: ACTIVITY_TYPE.flight,
    travelOptionType: TRAVEL_OPTION_TYPE.transport,
})

/**
 * Sets common properties for car and bus activities.
 *
 * This function initializes the default properties for a car or bus travel-related activity.
 *
 * @function
 * @param {Object} activityProps - The activity properties.
 * @param {ActivityCarBusState["common"]["activityType"]} activityProps.activityType - The type of activity from the car or bus state.
 * @param {ActivityCarBusState["common"]["travelOptionType"]} activityProps.travelOptionType - The type of travel option from the car or bus state.
 * @returns {Object} An object containing the standardized `activityType` and `travelOptionType` specific to car or bus activities.
 */
export const setCommonCarBusActivityProps = ():
    {
        activityType: ActivityCarBusState["common"]["activityType"],
        travelOptionType: ActivityCarBusState["common"]["travelOptionType"]
    } => ({
    activityType: ACTIVITY_TYPE.car_bus,
    travelOptionType: TRAVEL_OPTION_TYPE.transport,
})

/**
 * Sets common properties for hotel-related activities by mapping specific
 * activity and travel option types to standard hotel-related constants.
 *
 * @function
 * @param {Object} commonProps - The common properties of hotel activity state.
 * @param {string} commonProps.activityType - The activity type from the hotel activity state.
 * @param {string} commonProps.travelOptionType - The travel option type from the hotel activity state.
 * @returns {Object} An object containing the standardized hotel-related constants:
 * - `activityType`: The constant representing hotel activities.
 * - `travelOptionType`: The constant representing accommodation travel options.
 */
export const setCommonHotelActivityProps = ():
    {
        activityType: ActivityHotelState["common"]["activityType"],
        travelOptionType: ActivityHotelState["common"]["travelOptionType"]
    } => ({
    activityType: ACTIVITY_TYPE.hotel,
    travelOptionType: TRAVEL_OPTION_TYPE.accommodation,
})

/**
 * Updates the CO2 KGE value of an Activity. This is needed if an i.e. hotel has a duration of i.e. three days,
 * then the calculated CO2 KGE of this hotel is for three days. However, if the hotel is moved to one day (i.e.
 * in the modal from which the user can create a journey from travel components), the CO2 KGE emissions must
 * be adjusted to reflect only one day.
 *
 * @function
 * @param {AllActivityTypes} activity - The activity object containing emissions data and duration information.
 * @param {boolean} useOriginalDuration - A flag indicating whether to use the original duration of the activity or the adjusted (current) duration.
 *
 * The function processes the activity data based on its type (`flight`, `car_bus`, or `hotel`):
 * - If the activity is a flight, the function does not perform any adjustments.
 * - For `car_bus` activities, it calculates the emissions per day based on the selected duration type.
 * - For `hotel` activities, it determines the duration of the stay in nights, optionally using the original stay duration.
 *
 * If the `activityDuration` is valid and emissions data exists, the CO2 emissions value is adjusted to represent the equivalent emissions per day, rounded to two decimal places.
 *
 * The function modifies the input activity object and its emissions data directly.
 *
 * @throws Will throw an error if the `activityType` is unrecognized, as it enforces strict type inference.
 */
export const setCo2KgEToOneDayEquivalent = (activity: AllActivityTypes, useOriginalDuration: boolean) => {
    let activityDuration: number | null = null
    const {common: {activityType}} = activity
    switch (activityType) {
        case ACTIVITY_TYPE.flight:
            break;
        case ACTIVITY_TYPE.car_bus:
            if (isActivityCarBusState(activity)) {
                if (useOriginalDuration) {
                    activityDuration = activity.originalCountDays
                } else {
                    activityDuration = activity.countDays
                }
            }
            break;
        case ACTIVITY_TYPE.hotel:
            if (isActivityHotelState(activity)) activityDuration = getHotelDurationInNightsWithOptionalOriginalHotelStay(activity, useOriginalDuration)
            break;
        default:
            activityType satisfies never

    }
    if (
        activityDuration !== null &&
        activity.common.totalEmissions !== null &&
        activity.common.totalEmissions[0].emissionKgCO2e !== null
    ) {
        activity.common.totalEmissions[0].emissionKgCO2e = +(activity.common.totalEmissions[0].emissionKgCO2e / activityDuration!!).toFixed(2)
    }
}

/**
 * Updates any prop of an activity or an array of activities, if the activity or activities are moved to a day.
 *  Can be either used when a single activity is moved i.e. from the Travel Components Table to a day in a journey,
 *  or if in the journey screen multiple activities are moved via pressing the Alt Key.
 *
 * Uses the original request in common.originalRequest and updates the request in common.request to the newly used request
 * based on the day and the journey duration if recalculateActivityOnDifferentDate === true. The way the request is updated is activity specific.
 * This function either only returns the activity when it was actually updated and a recalculation is needed (returnOnlyWhenRecalculateTrue === true)
 * or returns the activity even if there was no updated on nay of the props (returnOnlyWhenRecalculateTrue === false)
 *
 * @function updateActivityRequestWithNewDate
 * @param {AllActivityTypes} activities - Object containing activity details, including its type and original request.
 * @param {DurationProps} journeyDuration - Duration properties, including start and end dates for the journey.
 * @param {number} day - The target day of the activity within the journey.
 * @param {boolean} returnOnlyWhenRecalculateTrue - Determines whether to return updated activities only when recalculation occurs.
 * @returns {AllActivityTypes} The updated activities object, potentially with new flight dates if recalculated.
 */
export const updateActivityRequestWithNewDate = (
    activities: AllActivityTypes,
    journeyDuration: DurationProps,
    day: ActivityCommonProperties['common']['day'],
    returnOnlyWhenRecalculateTrue: boolean,
): AllActivityTypes => {
    const {common: {activityType, originalRequest}} = activities
    if (activityType !== null && originalRequest !== null && day !== null) {
        const updatedOriginalRequest = cloneDeep(originalRequest)
        if (recalculateActivityOnDifferentDate(activityType)) {
            if (isActivityFlightState(activities)) {
                if (updatedOriginalRequest.every(isFlightCalculationRequestLeg)) {
                    let recalculate = false
                    const updatedFlightRequestLegs = updatedOriginalRequest.map(leg => {
                        const {flightDate} = leg
                        if (flightDate) {
                            const {dates} = journeyDuration
                            if (dates !== null) {
                                const {startDate} = dates
                                const newFlightDate = startDate.plus({days: day - 1})
                                leg.flightDate = convertDateTimeToFlightDate(newFlightDate)
                                recalculate = true
                            }
                        }
                        return leg
                    })
                    if (recalculate) {
                        activities.common.request = updatedFlightRequestLegs
                        if (returnOnlyWhenRecalculateTrue) return activities
                    }
                }
            }
        }
    }

    return activities
}
/**
 * Modifies a list of activities by recalculating and updating their dates when there is a change in the journey duration or date.
 *
 * This function creates a deep copy of the given activities array, then iterates through each activity, updating
 * its date information using the provided journey duration and current day of the activity.
 *
 * @param {AllActivityTypesArray} activities - An array containing all activity types to be updated with new date values.
 * @param {DurationProps} journeyDuration - The duration information used to recalculate and adjust the dates of activities.
 * @returns {void} This function does not return any value as it operates directly on the cloned activity array.
 */
export const addDatesToActivitiesWhenRecalculateOnDateChange = (
    activities: AllActivityTypesArray,
    journeyDuration: DurationProps
): void => cloneDeep(activities).forEach(updatedActivity => updateActivityRequestWithNewDate(updatedActivity, journeyDuration, updatedActivity.common.day, false))

/**
 * Updates the activities' dates based on the provided start date when a date change is detected for a specific activity type.
 *
 * This function processes an array of activities, evaluates each activity's type, and determines whether to update its date
 * information. If an activity is a hotel stay type and has a non-null day value, the function recalculates the start and
 * end dates for the hotel stay duration based on the provided start date and the activity's day number. Additionally, the
 * days or nights property is cleared.
 *
 * @param {AllActivityTypesArray} activities - An array of activity objects to be processed.
 * @param {DateTime} startDate - The starting date to calculate new dates for activities.
 */
export const addDatesToActivitiesWhenChangeDateOnDayChange = (
    activities: AllActivityTypesArray,
    startDate: DateTime
) => activities.forEach(activity => {
    const {common: {activityType, day}} = activity
    if (changeDateOnDayChange(activityType)) {
        if (isActivityHotelState(activity) && day !== null) {
            activity.hotelStayDuration.dates = {
                startDate: startDate.plus({days: day - 1}),
                endDate: startDate.plus({days: day})
            }
            activity.hotelStayDuration.daysOrNights = null
        }
    }
})

/**
 * Updates the duration of specific activities to one when modifying their activity day within a journey.
 * This function iterates over an array of activities and adjusts the duration for certain activity types
 * based on predefined rules. Activity types that require adjustment are handled differently depending on their type.
 *
 * @param {AllActivityTypesArray} activities - An array of activity objects, representing various types of activities
 *                                            within a journey.
 * @param {DurationProps} journeyDuration - An object representing the duration properties of the journey.
 *
 * The adjustments are as follows:
 * - For "flight" activities: No modification is performed.
 * - For "car_bus" activities: If the state is valid for modification, the duration is set to one using
 *                              `setCarBusDurationToOneWhenAddingToJourney`.
 * - For "hotel" activities: If the state is valid for modification, the duration is set to one using
 *                            `setDurationOfHotelToOneWhenAddingToJourney`, taking into consideration
 *                            the `journeyDuration` parameter.
 *
 * Activity types not listed are expected to satisfy a type-check constraint of `never`.
 */
export const updateDurationOfActivitiesToOneWhenChangingActivityDay = (activities: AllActivityTypesArray, journeyDuration: DurationProps) => activities.forEach(activity => {
    const {common: {activityType}} = activity
    if (adjustDurationOnAddToDay(activityType)) {
        switch (activityType) {
            case ACTIVITY_TYPE.flight:
                break;
            case ACTIVITY_TYPE.car_bus:
                if (isActivityCarBusState(activity)) setCarBusDurationToOneWhenAddingToJourney(activity)
                break;
            case ACTIVITY_TYPE.hotel:
                if (isActivityHotelState(activity)) setDurationOfHotelToOneWhenAddingToJourney(activity, journeyDuration)
                break;
            default:
                activityType satisfies never
        }
    }
})

/**
 * Adjusts the activities by removing the dates from their respective request objects if
 * a recalculation is performed due to a change in date. This function specifically targets
 * flight-related activities and removes their flight dates if necessary.
 *
 * @param {AllActivityTypesArray} activities - An array of activity objects containing information
 *      about activities and their associated details that might need date recalculation.
 */
export const removeDatesFromActivitiesWhenRecalculateOnDifferentDate = (activities: AllActivityTypesArray) => {
    activities.forEach(activity => {
        const {common: {activityType}} = activity
        if (changeDateOnDayChange(activityType)) {
            if (isActivityFlightState(activity)) activity.common.request?.forEach((leg, index) => {
                if (isFlightCalculationRequestLeg(leg)) leg.flightDate = null
            })
        }
    })
}

/**
 * Modifies the provided activities array by removing date information and adjusting the days or nights
 * to a default value for specific activity types when a day change occurs.
 *
 * @param {AllActivityTypesArray} activities - The array of activity objects to be checked and updated.
 */
export const removeDatesFromActivitiesWhenChangeDateOnDayChange = (activities: AllActivityTypesArray) => activities.forEach(activity => {
    const {common: {activityType}} = activity
    if (changeDateOnDayChange(activityType)) {
        if (isActivityHotelState(activity)) {
            activity.hotelStayDuration.dates = null
            activity.hotelStayDuration.daysOrNights = 1
        }
    }
})

/**
 * Adjusts and assigns dates to a given list of activities based on a starting date and journey duration.
 *
 * This function applies date adjustments according to specific rules for activities
 * when the starting date is modified or when recalculating based on the journey duration.
 *
 * @param {AllActivityTypesArray} activities - An array containing all activity types that require date adjustments.
 * @param {DateTime} startDate - The initial starting date from which activity dates are calculated.
 * @param {DurationProps} journeyDuration - The duration of the journey, used for recalculating activity dates.
 */
export const addDatesToActivities = (activities: AllActivityTypesArray, startDate: DateTime, journeyDuration: DurationProps) => {
    addDatesToActivitiesWhenChangeDateOnDayChange(activities, startDate)
    addDatesToActivitiesWhenRecalculateOnDateChange(activities, journeyDuration)
}

/**
 * Removes the route and resets the waypoints to the initial state for a CarBus activity.
 *
 * This function modifies the `route` and `waypoints` properties of a given CarBus activity
 * and optionally applies these changes to an array of activities.
 *
 * @param {AllActivityTypes} activity - The activity to modify. If it is a CarBus activity, its `route`
 * will be set to `null` and its `waypoints` will be reset to `INITIAL_WAYPOINTS`.
 *
 * @param {AllActivityTypesArray} [activities] - An optional array of activities. For each CarBus activity
 * in this array, the `route` will be set to `null` and the `waypoints` will be reset to `INITIAL_WAYPOINTS`.
 */
export const removeRouteAndWaypointsFromCarBusActivity = (
    activity: AllActivityTypes,
    activities?: AllActivityTypesArray,
) => {
    if (isActivityCarBusState(activity)) {
        activity.route = null
        activity.waypoints = INITIAL_WAYPOINTS
    }
    if (activities) {
        activities.forEach(activity => {
            if (isActivityCarBusState(activity)) {
                activity.route = null
                activity.waypoints = INITIAL_WAYPOINTS
            }
        })
    }
}

/**
 * Updates any duration related prop in either one Activity or an array of Activities according to the journey duration.
 * This function is needed when an Activity is i.e. moved from the Travel Components table to a journey, because in
 * the Travel Components table, an Activity can span over several days wheres in the journey, each Activity is only
 * "one day long".
 *
 * This function modifies the duration of activities to ensure consistency when the day of the activity changes.
 * If the journey duration includes exact dates, it updates the activities with the specified start date.
 * If exact dates are not provided, the function removes date-related properties from the activities.
 *
 * @param {DurationProps} journeyDuration - The object representing the duration of the journey, which may include exact dates.
 * @param {AllActivityTypes} activity - The activity whose duration-related properties need to be updated.
 * @param {AllActivityTypesArray} [activities] - An optional array of activities to be updated with or without date properties based on the journey duration.
 */
export const updateAnyDurationRelatedActivityProp = (
    journeyDuration: DurationProps,
    activity: AllActivityTypes,
    activities?: AllActivityTypesArray,
) => {
    updateDurationOfActivitiesToOneWhenChangingActivityDay([activity], journeyDuration)
    if (hasExactDates(journeyDuration)) {
        if (activities) addDatesToActivitiesWhenChangeDateOnDayChange(activities, journeyDuration.dates!!.startDate)
    } else {
        if (activities) removeDatesFromActivitiesWhenChangeDateOnDayChange(activities)
    }
}

/**
 * Updates the distance-related properties of a given activity by redistributing the distance
 * values over the count of days associated with the activity, if certain conditions are met.
 *
 * @param {AllActivityTypes} activity - The activity object containing details such as
 *                                      calculated distance, manually entered distance,
 *                                      and the count of days for redistribution.
 */
export const updateAnyDistanceRelatedActivityProp = (activity: AllActivityTypes) => {
    if (adjustDistanceOnAddToDay(activity)) {
        const {calculatedDistanceKm, manualEnteredDistanceKm, countDays} = activity
        if (calculatedDistanceKm !== null) activity.calculatedDistanceKm = calculatedDistanceKm / countDays
        if (manualEnteredDistanceKm !== null) activity.manualEnteredDistanceKm = manualEnteredDistanceKm / countDays
    }
}


/**
 * Removes the returnId property from the provided activity object if it has a return route.
 *
 * @function
 * @name removeReturnReference
 * @param {AllActivityTypes} activity - The activity object to be processed.
 * @description This function checks if the given activity includes return route properties by
 * invoking the `isActivityWithReturnRouteProps` helper. If the activity has a return route,
 * its `returnId` property will be set to null.
 */
export const removeReturnReference = (activity: AllActivityTypes) => {
    if (isActivityWithReturnRouteProps(activity)) activity.returnId = null
}

/**
 * Wrapper function that is called when moving an Activity from the "Travel Components State" to the "Journey State".
 * In "Travel Component State" an Activity can be i.e. several days long. However, in a journey, an Activity only occupies
 * one day. When moving an Activity from the "Travel Component State" to the "Journey State", the (in this case)
 * duration must be adjusted. Alongside the duration, the calculated co2 must be adjusted since the co2 is for the whole
 * duration and not for one single day
 *
 * This function performs the following operations on the provided activity:
 * 1. Sets the CO2 in kilograms equivalent for a one-day equivalent activity.
 * 2. Updates any properties related to distance within the activity.
 * 3. Removes any references indicating the activity is a return trip.
 *
 * @param {AllActivityTypes} activity - The activity object to be transformed into a journey state.
 */
export const transformActivityToJourneyState = (
    activity: AllActivityTypes,
) => {
    setCo2KgEToOneDayEquivalent(activity, true)
    updateAnyDistanceRelatedActivityProp(activity)
    removeReturnReference(activity)
}

/**
 * Wrapper function that is called when moving an Activity from the "Travel Components State" to the "Journey State" in the
 * create journey from travel components dialogue. Compared to changeActivityFromTravelComponentsStateToJourneyState(),
 * this function reduces the duration of an Activity to one day/night.
 *
 * @param {DurationProps} journeyDuration - The duration properties of the journey.
 * @param {AllActivityTypes} activity - The activity that needs to be converted
 *                                       into a journey state.
 *
 * The function performs the following operations:
 * 1. Updates properties related to activity duration based on the given journey duration.
 * 2. Transforms the activity into a journey state mode.
 * 3. Generates a unique identifier for the activity.
 */
export const activityToJourneyStateWhenCreateJourneyFromTravelComponents = (
    journeyDuration: DurationProps,
    activity: AllActivityTypes,
) => {
    updateAnyDurationRelatedActivityProp(journeyDuration, activity)
    transformActivityToJourneyState(activity)
    activity.common.id = uuidv4()
}